import React, { useState, useEffect, useRef } from "react";
import { X, Mail, ChevronDown, Info, SquareArrowOutUpRight } from "lucide-react";
import Toast from "../../common/Toast/Toast";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import { IoCloseCircleOutline } from "react-icons/io5";
import { emailccRegex } from "../../configs/App.Regex";
import { ImAttachment } from "react-icons/im";
import { FiFileText } from "react-icons/fi";
import WarningIcon from "../../resources/icons/warning-info.svg";

const EmailPreviewModal = ({
  recipient,
  recipients,
  isOpen,
  onClose,
  isMultipleSelect,
  toEmails,
  currentToEmail,
  toEmailError,
  onToEmailChange,
  onAddToEmail,
  onRemoveToEmail,
  onPasteToEmail,
  ccEmails,
  setCcEmails,
  currentCcEmail,
  ccEmailError,
  onCcEmailChange,
  onAddCcEmail,
  onRemoveCcEmail,
  onPasteCcEmail,
}) => {
  const [selectedRecipientId, setSelectedRecipientId] = useState("");
  const [emailMetadata, setEmailMetadata] = useState(null);
  const [emailPreview, setEmailPreview] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recipientToEmails, setRecipientToEmails] = useState({});
  const [recipientCcEmails, setRecipientCcEmails] = useState({});
  const [currentMultiToEmail, setCurrentMultiToEmail] = useState("");
  const [currentMultiCcEmail, setCurrentMultiCcEmail] = useState("");
  const [multiToEmailError, setMultiToEmailError] = useState("");
  const [multiCcEmailError, setMultiCcEmailError] = useState("");
  const [activeField, setActiveField] = useState(null); 
  const toInputRef = useRef(null);
  const ccInputRef = useRef(null); 
  const [isEmailIdentity, setIsEmailIdentity] = useState(false);
  const [emailIdentityMessage, setIsEmailIdentityMessage] = useState("");

  useEffect(() => {
    if (isOpen && isMultipleSelect && recipients.length > 0) {
      const initialEmails = {};
      const initialCcEmailsMap = {};
      recipients.forEach((recipient) => {
        initialEmails[recipient.id] = [recipient.email];
        initialCcEmailsMap[recipient.id] = [];
      });
      setRecipientToEmails(initialEmails);
      setRecipientCcEmails(initialCcEmailsMap);
      setSelectedRecipientId(recipients[0].id);
    }
  }, [isOpen, isMultipleSelect, recipients]);

  useEffect(() => {
    if (isOpen && !isMultipleSelect && recipient) {
      fetchEmailMetadata(recipient.id);
      fetchEmailPreview(recipient.id);
    }
  }, [isOpen, isMultipleSelect, recipient]);

  useEffect(() => {
    if (selectedRecipientId) {
      fetchEmailMetadata(selectedRecipientId);
      fetchEmailPreview(selectedRecipientId);
    }
  }, [selectedRecipientId]);

  useEffect(() => {
    if (isMultipleSelect && selectedRecipientId) {
      fetchEmailMetadata(selectedRecipientId);
      fetchEmailPreview(selectedRecipientId);
    }
  }, [selectedRecipientId]);

  const fetchEmailMetadata = async (credentialId) => {
    try {
      const response = await GET(
        `${EntityConstants.POST_ISSUANCE_EMAIL_METADATA}/${credentialId}`
      );
      const data = await response.json();
      setEmailMetadata(data.email_templates);
      setIsEmailIdentity(data.email_templates.is_email_identity);
      setIsEmailIdentityMessage(data.email_templates.message);

      if (
        data.email_templates?.email_cc &&
        Array.isArray(data.email_templates.email_cc)
      ) {
        if (isMultipleSelect) {
          // Only set initial CC emails if they haven't been modified
          if (!recipientCcEmails[credentialId]?.length) {
            setRecipientCcEmails((prev) => ({
              ...prev,
              [credentialId]: [...data.email_templates.email_cc],
            }));
          }
        } else {
          // For single recipient mode
          if (!ccEmails.length) {
            setCcEmails([...data.email_templates.email_cc]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching email metadata:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch email data",
      });
    }
  };

  const fetchEmailPreview = async (credentialId) => {
    try {
      setIsLoading(true);
      const response = await GET(
        `${EntityConstants.POST_ISSUANCE_EMAIL_PREVIEW}/${credentialId}`
      );
      const html = await response.text();
      setEmailPreview(html);
    } catch (error) {
      console.error("Error fetching email preview:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch email preview",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmails = async () => {
    try {
      setIsSending(true);

      let payload;
      if (isMultipleSelect) {
        payload = {
          notification_type: "email",
          credentials_id: recipients.map((r) => r.id),
          credentials_with_cc: recipients.map((recipient) => ({
            credentials_id: recipient.id,
            to: recipientToEmails[recipient.id],
            cc: recipientCcEmails[recipient.id],
            bcc: [],
          })),
        };
      } else {
        payload = {
          notification_type: "email",
          credentials_id: [recipient.id],
          credentials_with_cc: [
            {
              credentials_id: recipient.id,
              to: toEmails,
              cc: ccEmails,
              bcc: [],
            },
          ],
        };
      }
      const response = await POST(EntityConstants.SEND_EMAIL, payload);
      const data = await response.json();

      if (response.ok) {
        Toast({
          type: "success",
          title: "Success",
          message: isMultipleSelect
            ? "Emails sent successfully!"
            : "Email sent successfully!",
        });
        onClose();
      } else {
        throw new Error(data.message || "Failed to send email(s)");
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to send email(s)",
      });
      console.error("Send email error:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleRecipientEmailChange = (e) => {
    const email = e.target.value;
    setCurrentMultiToEmail(email);
    if (!email || emailccRegex.test(email)) {
      setMultiToEmailError("");
    }
  };

  const handleRecipientCcEmailChange = (e) => {
    const email = e.target.value;
    setCurrentMultiCcEmail(email);
    if (!email || emailccRegex.test(email)) {
      setMultiCcEmailError("");
    }
  };

  const handleAddMultiToEmail = () => {
    if (activeField !== "to") return;
    if (emailccRegex.test(currentMultiToEmail)) {
      if (
        recipientToEmails[selectedRecipientId]?.includes(currentMultiToEmail)
      ) {
        setMultiToEmailError("* Email already exists");
      } else {
        setRecipientToEmails((prev) => ({
          ...prev,
          [selectedRecipientId]: [
            ...(prev[selectedRecipientId] || []),
            currentMultiToEmail,
          ],
        }));
        setCurrentMultiToEmail("");
        setMultiToEmailError("");
      }
    } else if (currentMultiToEmail) {
      setMultiToEmailError("* Please enter a valid email");
    }
  };

  const handleAddMultiCcEmail = () => {
    if (activeField !== "cc") return;
    if (emailccRegex.test(currentMultiCcEmail)) {
      if (
        recipientCcEmails[selectedRecipientId]?.includes(currentMultiCcEmail)
      ) {
        setMultiCcEmailError("* Email already exists");
      } else {
        setRecipientCcEmails((prev) => ({
          ...prev,
          [selectedRecipientId]: [
            ...(prev[selectedRecipientId] || []),
            currentMultiCcEmail,
          ],
        }));
        setCurrentMultiCcEmail("");
        setMultiCcEmailError("");
      }
    } else if (currentMultiCcEmail) {
      setMultiCcEmailError("* Please enter a valid email");
    }
  };

  const handleRemoveMultiToEmail = (emailToRemove) => {
    setRecipientToEmails((prev) => ({
      ...prev,
      [selectedRecipientId]: prev[selectedRecipientId].filter(
        (email) => email !== emailToRemove
      ),
    }));
  };

  const handleRemoveMultiCcEmail = (emailToRemove) => {
    setRecipientCcEmails((prev) => ({
      ...prev,
      [selectedRecipientId]: prev[selectedRecipientId].filter(
        (email) => email !== emailToRemove
      ),
    }));
  };

  const handleSendClick = () => {
    if (isMultipleSelect) {
      setShowConfirmation(true);
    } else {
      handleSendEmails();
    }
  };

  const getTotalEmailCounts = () => {
    if (isMultipleSelect) {
      const totalTo = Object.values(recipientToEmails).reduce(
        (sum, emails) => sum + emails.length,
        0
      );
      const totalCc = Object.values(recipientCcEmails).reduce(
        (sum, emails) => sum + emails.length,
        0
      );
      return { totalTo, totalCc };
    }
    return {
      totalTo: toEmails.length,
      totalCc: ccEmails.length,
    };
  };

  const { totalTo, totalCc } = getTotalEmailCounts();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">
            Send Email Notification{isMultipleSelect ? "s" : ""}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row overflow-hidden">
          <div className="w-full lg:w-1/2 border-r border-gray-200 p-6 overflow-y-auto">
            {isMultipleSelect && (
              <div className="mb-6">
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    <p>
                      Notifications will be sent to {recipients.length}{" "}
                      recipients
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <label htmlFor="recipient-select" className="block text-sm font-medium text-cfPrimary mb-2">
                    Preview For:
                  </label>
                  <div className="relative">
                    <select
                      id="recipient-select"
                      value={selectedRecipientId}
                      onChange={(e) => setSelectedRecipientId(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg bg-blue-50 text-cfPrimary font-medium appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    >
                      {recipients.map((recipient) => (
                        <option key={recipient.id} value={recipient.id}>
                          {recipient.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-cfPrimary pointer-events-none" />
                  </div>
                  <p className="mt-2 text-sm text-cfPrimary">
                    Select a recipient to preview their email and modify the
                    recipient addresses.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label htmlFor="from-email" className="block text-sm font-medium text-gray-700 mb-2">
                  From:
                </label>
                <input
                  id="from-email"
                  type="text"
                  value={emailMetadata?.from_email || ""}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              {isEmailIdentity === false && (
                    <span className="flex">
                      <div className="flex w-full text-start justify-start items-center gap-2 p-2 bg-cfEmailWarningContainerBg rounded-lg">
                        <img
                          alt="Warning"
                          src={WarningIcon}
                          height={35}
                          width={35}
                        />
                        <div className="text-cfEmailWarningTextBg text-sm">
                          {emailIdentityMessage}
                        </div>
                      </div>
                    </span>
                  )}

              <div className="email-to">
                <label htmlFor="to-email-input" className="block text-sm font-medium text-gray-700 mb-2">
                  To:
                </label>
                {isMultipleSelect ? (
                  <div
                    className="flex border-[1.5px] border-gray-300 rounded-none w-full"
                    onClick={() => {
                      setActiveField("to");
                      if (!currentMultiToEmail) {
                        toInputRef.current?.focus();
                      } else {
                        toInputRef.current?.focus();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setActiveField("to");
                        toInputRef.current?.focus();
                      }
                    }}
                  >
                    <div className="flex items-start gap-2 flex-wrap p-2 bg-white rounded-none min-h-[50px]">
                      {recipientToEmails[selectedRecipientId]?.map((email) => (
                        <div
                          key={email}
                          className="bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1 rounded-full flex gap-1 items-center"
                        >
                          {email}
                          <button
                            className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                            onClick={() => handleRemoveMultiToEmail(email)}
                          >
                            <IoCloseCircleOutline size={20} />
                          </button>
                        </div>
                      ))}
                      <div className="w-fit flex gap-2 items-center">
                        <input
                          ref={toInputRef}
                          id="to-email-input"
                          type="email"
                          placeholder="recipient@example.com"
                          value={currentMultiToEmail}
                          onChange={handleRecipientEmailChange}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleAddMultiToEmail()
                          }
                          className={`${
                            currentMultiToEmail
                              ? "border-2 border-[#3538cd]"
                              : "border-0"
                          } focus:border-2 focus:border-[#3538cd] rounded-full focus:outline-[#3538cd] text-gray-700 ${
                            !currentMultiToEmail
                              ? "placeholder:text-gray-300"
                              : "placeholder:text-gray-400"
                          } px-2 py-1`}
                        />
                        {!multiToEmailError && currentMultiToEmail && (
                          <button
                            className="rounded-full bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1"
                            onClick={handleAddMultiToEmail}
                          >
                            +Add
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex border-[1.5px] border-gray-300 rounded-none w-full">
                    <div className="flex items-start gap-2 flex-wrap p-2 bg-white rounded-none min-h-[50px]">
                      {toEmails.map((email) => (
                        <div
                          key={email}
                          className="bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1 rounded-full flex gap-1 items-center"
                        >
                          {email}
                          <button
                            className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                            onClick={() => onRemoveToEmail(email)}
                          >
                            <IoCloseCircleOutline size={20} />
                          </button>
                        </div>
                      ))}
                      <div className="w-fit flex gap-2 items-center">
                        <input
                          id="to-email-input"
                          type="email"
                          placeholder="recipient@example.com"
                          value={currentToEmail}
                          onChange={onToEmailChange}
                          onFocus={() => setActiveField("to")}
                          onKeyDown={(e) => e.key === "Enter" && onAddToEmail()}
                          onPaste={onPasteToEmail}
                          className={`${
                            currentToEmail
                              ? "border-2 border-[#3538cd]"
                              : "border-0"
                          } focus:border-2 focus:border-[#3538cd] rounded-full focus:outline-[#3538cd] text-gray-700 ${
                            !currentToEmail
                              ? "placeholder:text-gray-300"
                              : "placeholder:text-gray-400"
                          } px-2 py-1`}
                        />
                        {!toEmailError && currentToEmail && (
                          <button
                            className="rounded-full bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1"
                            onClick={onAddToEmail}
                          >
                            +Add
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {(toEmailError || multiToEmailError) && (
                  <p className="text-red-500 mt-1 text-sm">
                    {toEmailError || multiToEmailError}
                  </p>
                )}
              </div>

              <div className="email-cc">
                <label htmlFor="cc-email-input" className="block text-sm font-medium text-gray-700 mb-2">
                  CC:
                </label>
                {isMultipleSelect ? (
                  <div
                    className="flex border-[1.5px] border-gray-300 rounded-none w-full"
                    onClick={() => {
                      setActiveField("cc");
                      if (!currentMultiCcEmail) {
                        ccInputRef.current?.focus();
                      } else {
                        ccInputRef.current?.focus();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setActiveField("cc");
                        ccInputRef.current?.focus();
                      }
                    }}
                  >
                    <div className="flex items-start gap-2 flex-wrap p-2 bg-white rounded-none min-h-[50px]">
                      {recipientCcEmails[selectedRecipientId]?.map((email) => (
                        <div
                          key={email}
                          className="bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1 rounded-full flex gap-1 items-center"
                        >
                          {email}
                          <button
                            className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                            onClick={() => handleRemoveMultiCcEmail(email)}
                          >
                            <IoCloseCircleOutline size={20} />
                          </button>
                        </div>
                      ))}
                      <div className="w-fit flex gap-2 items-center">
                        <input
                          ref={ccInputRef}
                          id="cc-email-input"
                          type="email"
                          placeholder="cc@example.com"
                          value={currentMultiCcEmail}
                          onChange={handleRecipientCcEmailChange}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleAddMultiCcEmail()
                          }
                          className={`${
                            currentMultiCcEmail
                              ? "border-2 border-[#3538cd]"
                              : "border-0"
                          } focus:border-2 focus:border-[#3538cd] rounded-full focus:outline-[#3538cd] text-gray-700 ${
                            !currentMultiCcEmail
                              ? "placeholder:text-gray-300"
                              : "placeholder:text-gray-400"
                          } px-2 py-1`}
                        />
                        {!multiCcEmailError && currentMultiCcEmail && (
                          <button
                            className="rounded-full bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1"
                            onClick={handleAddMultiCcEmail}
                          >
                            +Add
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex border-[1.5px] border-gray-300 rounded-none w-full">
                    <div className="flex items-start gap-2 flex-wrap p-2 bg-white rounded-none min-h-[50px]">
                      {ccEmails.map((email) => (
                        <div
                          key={email}
                          className="bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1 rounded-full flex gap-1 items-center"
                        >
                          {email}
                          <button
                            className="flex items-center justify-center rounded-full text-lg h-6 w-6 outline-none focus:outline-none focus:ring-0"
                            onClick={() => onRemoveCcEmail(email)}
                          >
                            <IoCloseCircleOutline size={20} />
                          </button>
                        </div>
                      ))}
                      <div className="w-fit flex gap-2 items-center">
                        <input
                          id="cc-email-input"
                          type="email"
                          placeholder="cc@example.com"
                          value={currentCcEmail}
                          onChange={onCcEmailChange}
                          onFocus={() => setActiveField("cc")}
                          onKeyDown={(e) => e.key === "Enter" && onAddCcEmail()}
                          onPaste={onPasteCcEmail}
                          className={`${
                            currentCcEmail
                              ? "border-2 border-[#3538cd]"
                              : "border-0"
                          } focus:border-2 focus:border-[#3538cd] rounded-full focus:outline-[#3538cd] text-gray-700 ${
                            !currentCcEmail
                              ? "placeholder:text-gray-300"
                              : "placeholder:text-gray-400"
                          } px-2 py-1`}
                        />
                        {!ccEmailError && currentCcEmail && (
                          <button
                            className="rounded-full bg-[#3538cd] hover:bg-[#3538cd]/90 text-white px-2 py-1"
                            onClick={onAddCcEmail}
                          >
                            +Add
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {(ccEmailError || multiCcEmailError) && (
                  <p className="text-red-500 mt-1 text-sm">
                    {ccEmailError || multiCcEmailError}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject:
                </label>
                <input
                  id="email-subject"
                  type="text"
                  value={emailMetadata?.email_subject || ""}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
            </div>
          </div>
          <hr />

          <div className="w-full flex flex-col lg:w-1/2 p-6 overflow-y-auto">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Email Preview
              </h3>
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div
                  className="prose prose-sm min-w-[800px] lg:min-w-full max-w-none overflow-x-auto"
                  style={{ 
                    color: 'inherit'
                  }}
                  dangerouslySetInnerHTML={{ __html: emailPreview.replace(
                      /<a href=/g, 
                      '<a style="color: blue; text-decoration: underline;" href='
                    ) 
                  }}
                />
              )}
            </div>
            {emailMetadata?.attached_file && emailMetadata?.attachment && (
              <div>
                <span className="flex border-t-[1.5px] pt-3">
                  <div className="w-[9.6rem] flex items-center">
                    <p className="flex items-center font-semibold gap-2">
                      {" "}
                      <ImAttachment /> Attachment
                    </p>
                    <p className="font-semibold text-gray-300 px-3">|</p>
                  </div>
                  <button
                    onClick={() =>
                      window.open(emailMetadata?.attached_file, "_blank")
                    }
                    className="border-[1.5px] rounded-[0.63rem] flex gap-2 items-center p-2 text-sm hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none"
                    title="Click to preview attachment"
                  >
                    <FiFileText className="size-5 text-cfPrimaryDark" />
                    <span>{emailMetadata?.attachment}</span>
                    <SquareArrowOutUpRight
                      strokeWidth={2.2}
                      className="size-4 text-gray-600"
                    />
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSendClick}
            disabled={isSending}
            className={`px-4 py-2 bg-cfPrimaryDark text-white rounded-md hover:bg-cfPrimary transition-colors flex items-center gap-2 ${
              isSending ? "opacity-75 cursor-not-allowed" : ""
            }`}
          >
            <Mail className="h-4 w-4" />
            {isSending
              ? "Sending..."
              : `Send Email${isMultipleSelect ? "s" : ""}`}
          </button>
        </div>
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Send
            </h3>
            <div className="text-gray-600 space-y-2 mb-6">
              <p>
                You&apos;re about to send email notifications to {recipients.length}{" "}
                {recipients.length > 1 ? "recipients" : "recipient"} :
              </p>
              <ul className="list-disc list-inside pl-4 space-y-1">
                <li>Total &quot;To&quot; addresses: {totalTo}</li>
                <li>Total &quot;CC&quot; addresses: {totalCc}</li>
              </ul>
              <p className="mt-4">Would you like to continue?</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  handleSendEmails();
                }}
                className="px-4 py-2 bg-cfPrimaryDark text-white rounded-md hover:bg-cfPrimary transition-colors"
              >
                Yes, Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailPreviewModal;
